import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  OnChangeFn,
  PaginationState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { Text } from "@chakra-ui/react";
import { AttachmentIcon } from "@chakra-ui/icons";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { ReceivedEmailMessagesOrderBy } from "__generated__/gql/graphql";
import useFixedLayout from "components/Shared/ChakraReactTable/useFixedLayout";
import {
  useChakraReactTableProps,
  usePrepApolloData,
} from "components/Shared/ChakraReactTable/utils";
import {
  formatIntegerCell,
  formatJustifiedDateTimeCell,
} from "components/Shared/ChakraReactTable/formatters";
import usePagination from "components/Shared/ChakraReactTable/usePagination";
import useSelectableRow from "components/Shared/ChakraReactTable/useSelectableRow";
import ScrollContainer from "components/Shared/ChakraReactTable/ScrollContainer";
import ChakraReactTable from "components/Shared/ChakraReactTable";
import NumResultsLabel from "components/Shared/ChakraReactTable/NumResultsLabel";
import PaginationControls from "components/Shared/ChakraReactTable/PaginationControls";
import TableControls from "components/Shared/ChakraReactTable/TableControls";
import TableControlsFooter from "components/Shared/ChakraReactTable/TableControlsFooter";
import useStickyHeader from "components/Shared/ChakraReactTable/useStickyHeader";
import Participants from "components/Shared/SelectedEmail/Participants";
import { GQLOrderByEnumsById } from "components/Shared/ChakraReactTable/useGQLSorting";

const fragment = gql(/* GraphQL */ `
  fragment EmailTableColumns on ReceivedEmailMessage {
    emailMessageId
    emailMessage {
      id
      sentAt
      subject
      originalFrom {
        id
        ...EmailMessageParticipants
      }
    }
    parsedReceivedEmailMessage {
      receivedEmailMessageId
      receivedEmailMessageAttachments(
        filter: { isRelated: { distinctFrom: true } }
      ) {
        totalCount
      }
    }
  }
`);

export type EmailTableRow = FragmentType<typeof fragment>;

function fromFragment(row: EmailTableRow) {
  return getFragmentData(fragment, row);
}

const col = createColumnHelper<EmailTableRow>();

export const BASE_COLUMNS = [
  col.accessor((r) => fromFragment(r).emailMessage?.sentAt, {
    id: "receivedAt",
    header: "Received at",
    cell: formatJustifiedDateTimeCell,
    size: 205,
    meta: { headerProps: { textAlign: "center" } },
  }),
  col.accessor((r) => fromFragment(r).emailMessage?.subject, {
    id: "subject",
    header: "Subject",
    size: 250,
    meta: { props: { isTruncated: true } },
  }),
  col.accessor(
    (r) =>
      fromFragment(r).parsedReceivedEmailMessage
        ?.receivedEmailMessageAttachments?.totalCount,
    {
      id: "numAttachments",
      header: () => <AttachmentIcon />,
      cell: formatIntegerCell,
      size: 50,
      enableSorting: false,
      meta: {
        headerProps: { textAlign: "right" },
        cellProps: { isNumeric: true },
      },
    },
  ),
  col.accessor((r) => fromFragment(r).emailMessage?.originalFrom, {
    id: "from",
    header: "From",
    cell: (cell) => {
      const emp = cell.getValue();
      return <Participants participants={emp && [emp]} />;
    },
    size: 225,
    enableSorting: false,
  }),
];

export const BASE_ORDER_BY: GQLOrderByEnumsById<ReceivedEmailMessagesOrderBy> =
  {
    receivedAt: [
      ReceivedEmailMessagesOrderBy.EMAIL_MESSAGE__SENT_AT_ASC,
      ReceivedEmailMessagesOrderBy.EMAIL_MESSAGE__SENT_AT_DESC,
    ],
    subject: [
      ReceivedEmailMessagesOrderBy.EMAIL_MESSAGE__SUBJECT_ASC,
      ReceivedEmailMessagesOrderBy.EMAIL_MESSAGE__SUBJECT_DESC,
    ],
  };

export type EmailTableProps<T extends EmailTableRow> = {
  data: T[] | null | undefined;
  totalCount: number | null | undefined;
  isLoading: boolean;
  selected?: string | null;
  onSelectedChange: (emailId: string) => void;
  onRefetch: () => Promise<unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns?: ColumnDef<T, any>[];
  // Pagination and sorting are managed externally
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  // Custom props for the table
  height?: string;
};

export default function EmailTable<T extends EmailTableRow>({
  data,
  totalCount,
  isLoading,
  selected,
  onSelectedChange,
  onRefetch,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns = BASE_COLUMNS as ColumnDef<T, any>[],
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  height = "24rem",
}: EmailTableProps<T>) {
  const { getPageCount } = usePagination({ pageSize: pagination.pageSize });

  const tableInstance = useReactTable({
    columns,
    data: usePrepApolloData(data),
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    state: { pagination, sorting },
    pageCount: getPageCount(totalCount),
  });

  const tableProps = useChakraReactTableProps<T>([
    useFixedLayout(tableInstance),
    useStickyHeader(),
    useSelectableRow({
      selected,
      getIdForRow: (row) => fromFragment(row.original).emailMessageId,
      onSelectedChange,
    }),
    { tableProps: { variant: "condensed" } },
  ]);

  return (
    <>
      <ScrollContainer height={height} borderWidth={1} borderTopRadius=".25rem">
        <ChakraReactTable
          tableInstance={tableInstance}
          {...tableProps}
          isLoading={isLoading}
          noRowsPlaceholder={<Text>No emails found</Text>}
        />
      </ScrollContainer>
      <TableControlsFooter
        p={4}
        borderTopWidth={1}
        leftContent={
          <NumResultsLabel
            {...{ totalCount, isLoading }}
            resultTypeLabel="emails"
          />
        }
        rightContent={
          <TableControls {...{ isLoading }} onRefresh={onRefetch} />
        }
      >
        <PaginationControls {...{ tableInstance, isLoading }} />
      </TableControlsFooter>
    </>
  );
}
